import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Lang } from '@/contexts/LanguageContext';
import { localText } from '@/lib/localText';
import { supabase } from '@/integrations/supabase/client';
import logo from '@/assets/logo.webp';

interface DropdownGroup {
  label: string;
  items: { to: string; label: string; exact?: boolean }[];
}

const Header = () => {
  const { lang, t, switchLanguage } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [frenchEnabled, setFrenchEnabled] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const langRef = useRef<HTMLDivElement>(null);

  // Fetch enable_french setting
  useEffect(() => {
    supabase
      .from('site_settings')
      .select('enable_french')
      .limit(1)
      .single()
      .then(({ data }) => {
        if (data) setFrenchEnabled(data.enable_french);
      });
  }, []);

  // Close lang menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const lt = (en: string, ar: string, fr?: string) => localText(lang, en, ar, fr);

  const homeItem = { to: `/${lang}`, label: lt('Overview', 'نظرة عامة', 'Aperçu'), exact: true };

  const dropdownGroups: DropdownGroup[] = [
    {
      label: lt('The Firm', 'المؤسسة', 'L\'Institution'),
      items: [
        { to: `/${lang}/about`, label: t.nav.about },
        { to: `/${lang}/leadership`, label: t.nav.leadership },
        { to: `/${lang}/subsidiaries`, label: lt('Subsidiaries', 'الشركات التابعة', 'Filiales') },
        { to: `/${lang}/partners`, label: lt('Partners', 'الشركاء', 'Partenaires') },
        { to: `/${lang}/company-news`, label: lt('News', 'الأخبار', 'Actualités') },
      ],
    },
    {
      label: lt('Investment', 'الاستثمار', 'Investissement'),
      items: [
        { to: `/${lang}/strategy`, label: t.nav.strategy },
        { to: `/${lang}/projects`, label: t.nav.projects },
        { to: `/${lang}/mining-portfolio`, label: lt('Mining Portfolio', 'محفظة التعدين', 'Portefeuille Minier') },
        { to: `/${lang}/why-gold`, label: t.nav.whyGold },
      ],
    },
  ];

  const governanceItem = { to: `/${lang}/governance`, label: lt('Governance', 'الحوكمة', 'Gouvernance') };
  
  const marketIntelItem = { to: `/${lang}/gold-news`, label: lt('Insights', 'رؤى السوق', 'Perspectives') };
  const capitalAllocItem = { to: `/${lang}/investor`, label: lt('Investors', 'المستثمرون', 'Investisseurs') };
  const contactItem = { to: `/${lang}/contact`, label: t.nav.contact };

  const isActive = (to: string, exact?: boolean) =>
    exact ? location.pathname === to : location.pathname.startsWith(to);

  const isGroupActive = (group: DropdownGroup) =>
    group.items.some(item => isActive(item.to));

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  useEffect(() => {
    setOpenDropdown(null);
    setMobileOpen(false);
    setLangMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const availableLanguages: { code: Lang; label: string }[] = [
    { code: 'en', label: 'English' },
    { code: 'ar', label: 'العربية' },
    ...(frenchEnabled ? [{ code: 'fr' as Lang, label: 'Français' }] : []),
  ];

  const currentLangLabel = lang.toUpperCase();

  return (
    <header className={`sticky top-0 z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-charcoal backdrop-blur-xl shadow-[0_4px_30px_-5px_hsl(0_0%_0%/0.5)]' 
        : 'bg-charcoal backdrop-blur-sm'
    } border-b border-primary-foreground/5`}>
      <div className="container flex items-center justify-between h-20">
        {/* Logo */}
        <Link to={`/${lang}`} className="flex items-center gap-2.5 sm:gap-3.5 flex-1 min-w-0 group">
          <div className="relative shrink-0">
            <img src={logo} alt="A&M Gold Mining Fund" className="h-10 w-10 sm:h-11 sm:w-11 rounded-full ring-1 ring-secondary/30 group-hover:ring-secondary/60 transition-all duration-300" />
          </div>
          <div className="block min-w-0">
            <span className="text-primary-foreground font-display text-sm sm:text-base tracking-wide block leading-tight truncate">
              {t.nav.brandName}
            </span>
            <span className="text-secondary text-[10px] sm:text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase block truncate">
              {lt('Private Equity Regulated Fund', 'صندوق ملكية خاصة منظم', 'Fonds Réglementé de Capital-Investissement')}
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden xl:flex items-center gap-0.5">
          <Link
            to={homeItem.to}
            className={`px-4 py-2.5 text-[14px] font-semibold tracking-[0.12em] uppercase transition-all duration-300 ${
              isActive(homeItem.to, true)
                ? 'text-secondary'
                : 'text-primary-foreground/50 hover:text-primary-foreground'
            }`}
          >
            {homeItem.label}
          </Link>

          {dropdownGroups.map(group => (
            <div
              key={group.label}
              className="relative"
              onMouseEnter={() => handleMouseEnter(group.label)}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1.5 px-4 py-2.5 text-[14px] font-semibold tracking-[0.12em] uppercase transition-all duration-300 whitespace-nowrap ${
                  isGroupActive(group)
                    ? 'text-secondary'
                    : 'text-primary-foreground/50 hover:text-primary-foreground'
                }`}
              >
                {group.label}
                <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${openDropdown === group.label ? 'rotate-180' : ''}`} />
              </button>

              {openDropdown === group.label && (
                <div className="absolute top-full left-0 mt-2 min-w-[260px] bg-charcoal-light border border-primary-foreground/8 shadow-[0_20px_60px_-15px_hsl(0_0%_0%/0.6)] z-50 py-2 rounded-sm">
                  {group.items.map(item => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`block px-6 py-3 text-sm font-medium tracking-wide transition-all duration-200 ${
                        isActive(item.to)
                          ? 'text-secondary bg-secondary/5 border-l-2 border-secondary'
                          : 'text-primary-foreground/50 hover:text-primary-foreground hover:bg-primary-foreground/3 border-l-2 border-transparent'
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}

{[governanceItem, marketIntelItem, capitalAllocItem, contactItem].map(item => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-3 py-2.5 text-[12px] font-semibold tracking-[0.1em] uppercase transition-all duration-300 ${
                isActive(item.to)
                  ? 'text-secondary'
                  : 'text-primary-foreground/50 hover:text-primary-foreground'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {/* Language Switcher - Desktop only */}
          <div className="relative hidden xl:block" ref={langRef}>
            <button
              onClick={() => setLangMenuOpen(!langMenuOpen)}
              className="flex items-center gap-1.5 text-primary-foreground/55 hover:text-secondary transition-all duration-300 text-xs font-medium uppercase tracking-[0.15em]"
            >
              <Globe className="h-3.5 w-3.5" />
              <span>{currentLangLabel}</span>
              <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${langMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {langMenuOpen && (
              <div className="absolute top-full right-0 mt-2 min-w-[140px] bg-charcoal-light border border-primary-foreground/8 shadow-[0_20px_60px_-15px_hsl(0_0%_0%/0.6)] z-50 py-1 rounded-sm">
                {availableLanguages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => {
                      switchLanguage(l.code);
                      setLangMenuOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                      lang === l.code
                        ? 'text-secondary bg-secondary/5'
                        : 'text-primary-foreground/50 hover:text-primary-foreground hover:bg-primary-foreground/3'
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className="xl:hidden text-primary-foreground/60 hover:text-primary-foreground transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <nav className="xl:hidden bg-charcoal-light border-t border-primary-foreground/5 px-4 py-4 space-y-1">
          <Link
            to={homeItem.to}
            onClick={() => setMobileOpen(false)}
            className={`block px-4 py-3 text-sm font-semibold tracking-wide transition-colors ${
              isActive(homeItem.to, true) ? 'text-secondary' : 'text-primary-foreground/50 hover:text-secondary'
            }`}
          >
            {homeItem.label}
          </Link>

          {dropdownGroups.map(group => (
            <MobileDropdown
              key={group.label}
              group={group}
              isActive={isActive}
              onNavigate={() => setMobileOpen(false)}
            />
          ))}

          {[governanceItem, marketIntelItem, capitalAllocItem, contactItem].map(item => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={`block px-4 py-3 text-sm font-semibold tracking-wide transition-colors ${
                isActive(item.to) ? 'text-secondary' : 'text-primary-foreground/50 hover:text-secondary'
              }`}
            >
              {item.label}
            </Link>
          ))}

          {/* Language Switcher - Mobile */}
          <div className="border-t border-primary-foreground/10 mt-2 pt-3 px-4">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="h-3.5 w-3.5 text-secondary/60" />
              <span className="text-xs font-semibold tracking-wide uppercase text-primary-foreground/40">
                {lt('Language', 'اللغة', 'Langue')}
              </span>
            </div>
            <div className="flex gap-2">
              {availableLanguages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => {
                    switchLanguage(l.code);
                    setMobileOpen(false);
                  }}
                  className={`px-3 py-2 rounded text-sm font-medium transition-colors duration-200 ${
                    lang === l.code
                      ? 'text-secondary bg-secondary/10 border border-secondary/30'
                      : 'text-primary-foreground/50 hover:text-primary-foreground bg-primary-foreground/5 border border-transparent'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
};

const MobileDropdown = ({
  group,
  isActive,
  onNavigate,
}: {
  group: DropdownGroup;
  isActive: (to: string, exact?: boolean) => boolean;
  onNavigate: () => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold tracking-wide text-primary-foreground/50 hover:text-secondary transition-colors"
      >
        {group.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="pl-4 space-y-0.5 border-l border-secondary/20 ml-4">
          {group.items.map(item => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={`block px-4 py-2.5 text-sm tracking-wide transition-colors ${
                isActive(item.to) ? 'text-secondary' : 'text-primary-foreground/40 hover:text-secondary'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Header;
